import {
  createEmptyOtpDigits,
  isOtpInputBulkInsert,
  joinOtpDigits,
  otpDigitsFromBulkValue,
  PHONE_OTP_LENGTH,
  sanitizeOtpInput,
  sanitizeOtpPaste,
} from "@/lib/phoneAuth";
import { nextTick, ref, type Ref } from "vue";

type OtpCodeInputOptions = {
  digits: Ref<string[]>;
  inputRefs: Ref<Array<HTMLInputElement | null>>;
  onComplete: () => void | Promise<void>;
  onDigitsChange?: () => void;
};

export function useOtpCodeInput(options: OtpCodeInputOptions) {
  const autofillInputRef = ref<HTMLInputElement | null>(null);

  function clearAutofillInput() {
    const input = autofillInputRef.value;
    if (input) {
      input.value = "";
    }
  }

  function focusInput(index: number) {
    nextTick(() => {
      const input = options.inputRefs.value[index];
      input?.focus();
      input?.select();
    });
  }

  function applyDigits(nextDigits: string[]) {
    options.digits.value = nextDigits;
    options.onDigitsChange?.();

    if (joinOtpDigits(nextDigits).length === PHONE_OTP_LENGTH) {
      void options.onComplete();
      return;
    }

    const firstEmptyIndex = nextDigits.findIndex((digit) => !digit);
    focusInput(firstEmptyIndex === -1 ? nextDigits.length - 1 : firstEmptyIndex);
  }

  function applyBulkValue(rawValue: string) {
    const normalized = rawValue.replace(/\D/g, "");
    if (!normalized.length) {
      return;
    }

    applyDigits(otpDigitsFromBulkValue(rawValue));
    clearAutofillInput();
  }

  function onAutofillInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const readAndApply = () => applyBulkValue(target.value);
    readAndApply();
    nextTick(readAndApply);
  }

  function onDigitInput(index: number, event: Event) {
    const target = event.target as HTMLInputElement;
    const rawValue = target.value;

    if (isOtpInputBulkInsert(event) || rawValue.replace(/\D/g, "").length > 1) {
      applyBulkValue(rawValue);
      return;
    }

    const nextValue = sanitizeOtpInput(rawValue);
    const nextDigits = [...options.digits.value];
    nextDigits[index] = nextValue;
    options.digits.value = nextDigits;
    options.onDigitsChange?.();

    if (nextValue && index < nextDigits.length - 1) {
      focusInput(index + 1);
    }

    if (joinOtpDigits(nextDigits).length === PHONE_OTP_LENGTH) {
      void options.onComplete();
    }
  }

  function onPaste(event: ClipboardEvent) {
    const pasted = sanitizeOtpPaste(event.clipboardData?.getData("text") ?? "");
    if (!pasted.length) {
      return;
    }

    const nextDigits = createEmptyOtpDigits();
    pasted.forEach((digit, index) => {
      nextDigits[index] = digit;
    });
    applyDigits(nextDigits);
  }

  function onKeydown(index: number, event: KeyboardEvent) {
    if (event.key === "Backspace") {
      if (options.digits.value[index]) {
        const nextDigits = [...options.digits.value];
        nextDigits[index] = "";
        options.digits.value = nextDigits;
        options.onDigitsChange?.();
        return;
      }

      if (index > 0) {
        const nextDigits = [...options.digits.value];
        nextDigits[index - 1] = "";
        options.digits.value = nextDigits;
        options.onDigitsChange?.();
        focusInput(index - 1);
      }
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
    }

    if (event.key === "ArrowRight" && index < options.digits.value.length - 1) {
      event.preventDefault();
      focusInput(index + 1);
    }
  }

  return {
    autofillInputRef,
    focusInput,
    clearAutofillInput,
    onAutofillInput,
    onDigitInput,
    onPaste,
    onKeydown,
  };
}
